import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useAuth, RegisterData } from '@/contexts/AuthContext';
import { getCountries, getStates, Country, State } from '@/services/resourcesApi';
import { Loader2, Eye, EyeOff } from 'lucide-react';

const registerSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
  company_type: z.enum(['person', 'company'], { required_error: 'Type requis' }),
  street: z.string().min(1, 'Adresse requise'),
  city: z.string().min(1, 'Ville requise'),
  zip: z.string().min(1, 'Code postal requis'),
  country_id: z.number({ required_error: 'Pays requis' }),
  state_id: z.number({ required_error: 'État/Province requis' }),
  phone: z.string()
    .min(9, 'Le téléphone doit contenir exactement 9 chiffres')
    .max(9, 'Le téléphone doit contenir exactement 9 chiffres')
    .regex(/^\d{9}$/, 'Le téléphone doit contenir uniquement des chiffres'),
  vat: z.string().optional(),
  company_registry: z.string().optional(),
}).refine((data) => {
  if (data.company_type === 'company') {
    return data.vat && data.vat.length > 0 && data.company_registry && data.company_registry.length > 0;
  }
  return true;
}, {
  message: 'VAT et ICE sont requis pour les entreprises',
  path: ['vat'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

interface RegisterModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const RegisterModal = ({ isOpen, onOpenChange, onSuccess }: RegisterModalProps) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      company_type: 'person',
      country_id: 136,
    },
  });

  // Set Morocco as default and load states
  useEffect(() => {
    if (isOpen) {
      // Set Morocco as selected country directly
      const morocco = { id: 136, name: 'Maroc', code: 'MA', phone_code: '212' };
      setSelectedCountry(morocco);
    }
  }, [isOpen]);

  // Load states when country changes
  useEffect(() => {
    const loadStates = async (countryId: number) => {
      setIsLoadingStates(true);
      try {
        const response = await getStates(countryId);
        setStates(response.items);
        // Reset state selection when country changes
        form.setValue('state_id', undefined as any);
      } catch (error) {
        console.error('Failed to load states:', error);
        setStates([]);
      } finally {
        setIsLoadingStates(false);
      }
    };

    if (selectedCountry) {
      loadStates(selectedCountry.id);
    }
  }, [selectedCountry, form]);

  const onSubmit = async (data: RegisterFormData) => {
    const success = await register(data as RegisterData);
    if (success) {
      form.reset();
      onOpenChange(false);
      onSuccess?.();
    }
  };

  const formatPhoneNumber = (value: string, countryCode?: string) => {
    if (!countryCode) return value;
    
    // Remove existing country code if present
    const cleaned = value.replace(/^\+\d{1,4}[-\s]?/, '');
    
    // Add country code
    return `+${countryCode.replace('+', '')} ${cleaned}`;
  };

  const handleCountryChange = (countryId: string) => {
    const country = countries.find(c => c.id === parseInt(countryId));
    setSelectedCountry(country || null);
    form.setValue('country_id', parseInt(countryId));
    
    // Update phone number with country code
    if (country?.phone_code) {
      const currentPhone = form.getValues('phone');
      
      if (currentPhone && typeof currentPhone === 'string' && !currentPhone.startsWith('+')) {
        form.setValue('phone', formatPhoneNumber(currentPhone, country.phone_code));
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Créer un compte</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Company Type */}
            <FormField
              control={form.control}
              name="company_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type de compte</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex space-x-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="person" id="person" />
                        <Label htmlFor="person">Particulier</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="company" id="company" />
                        <Label htmlFor="company">Entreprise</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Nom complet / Mot de passe */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom complet / Raison sociale</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mot de passe</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          type={showPassword ? "text" : "password"} 
                          {...field} 
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Email / Téléphone */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Téléphone</FormLabel>
                    <FormControl>
                      <div className="flex">
                        {selectedCountry?.phone_code && (
                          <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-muted text-muted-foreground">
                            +{selectedCountry.phone_code.replace('+', '')}
                          </div>
                        )}
                        <Input 
                          {...field} 
                          className={selectedCountry?.phone_code ? "rounded-l-none" : ""}
                          placeholder="123456789"
                          maxLength={9}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                            field.onChange(value);
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Company-specific fields */}
            {form.watch('company_type') === 'company' && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="vat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>N° TVA</FormLabel>
                      <FormControl>
                        <Input placeholder="MA12345678" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company_registry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ICE</FormLabel>
                      <FormControl>
                        <Input placeholder="00123456700012" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Address */}
            <FormField
              control={form.control}
              name="street"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Adresse</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Ville / Code postal */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ville</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code postal</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* État/Province / Pays */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="state_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>État/Province</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(parseInt(value))} 
                      disabled={!selectedCountry || isLoadingStates}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={
                            !selectedCountry ? "Sélectionner un pays d'abord" :
                            isLoadingStates ? "Chargement..." : 
                            "Sélectionner"
                          } />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {states.map((state) => (
                          <SelectItem key={state.id} value={state.id.toString()}>
                            {state.name} {state.code && `(${state.code})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pays</FormLabel>
                    <FormControl>
                      <Input 
                        value="Maroc (MA)" 
                        disabled 
                        className="bg-muted cursor-not-allowed"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le compte
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterModal;